import React, { useMemo } from 'react'
import { ColorPicker, Form, TextField } from '@carsys/form'
import { Box, Grid, IconButton, Tooltip } from '@mui/material'
import { FieldArray } from 'formik'
import { useTranslation } from 'react-i18next'
import { NewEmployee } from './types.d'
import ClearIcon from '@mui/icons-material/Clear'
import AddIcon from '@mui/icons-material/Add'
import * as Yup from 'yup'
import { BaseStepLayout } from '../BaseStepLayout'
import { StepNavigation } from '../StepNavigation'
import { defaultStepFormProps } from '../types.d'
import { WithRunInBackgroundHelper } from '../use-should-run-in-background'
import { Subscriptions } from '@carsys/enums/subscription'

interface FormValues {
  Mechanics: NewEmployee[]
  prefilledEmployee?: NewEmployee
}

export const OnboardingAddMechanicsForm: React.FC<
  defaultStepFormProps & { onSubmit: (emp: NewEmployee[]) => void; prefilledEmployee?: NewEmployee; modules: Subscriptions[] }
> = ({
  onSubmit,
  prefilledEmployee,
  prevStep,
  isFirstStep,
  isPersisting,
  onSubmitSuccess,
  hideSkip,
  shouldRunInBackground,
  initialData,
  modules
}) => {
  const { t } = useTranslation()
  const tContext = { context: modules.includes(Subscriptions.Module_Portal) ? 'portal' : undefined }
  const initialValues = useMemo(
    () =>
      ({
        Mechanics: [],
        ...initialData,
        prefilledEmployee
      } as FormValues),
    [prefilledEmployee]
  )

  const requiredMessage = t('global.field_is_required')
  const validationSchema = Yup.object().shape({
    Mechanics: Yup.array().of(
      Yup.object().shape({
        EmailAddressWork: Yup.string().required(requiredMessage),
        Name: Yup.string().required(requiredMessage),
        FirstName: Yup.string().required(requiredMessage),
        NamePrefix: Yup.string(),
        ProfileColour: Yup.string()
      })
    )
  })

  const formSubmit = async (v: FormValues) => {
    onSubmit(v.Mechanics)
    return v
  }

  return (
    <BaseStepLayout
      title={t('onboarding.mechanics.mechanic_step_header', tContext)}
      subTitles={[t('onboarding.mechanics.mechanic_step_info', tContext)]}
    >
      <Grid spacing={2} container>
        <Grid item xs={12}>
          <Form
            onSubmit={formSubmit}
            onSubmitSuccess={onSubmitSuccess}
            initialValues={initialValues}
            NavigationConfirmation={null}
            validationSchema={validationSchema}
          >
            {({ values, isSubmitting, handleSubmit }) => (
              <WithRunInBackgroundHelper enabled={shouldRunInBackground} isSubmitting={isSubmitting} onSubmit={handleSubmit}>
                <FieldArray name='Mechanics'>
                  {({ remove, push }) => (
                    <>
                      {values.prefilledEmployee && (
                        <Box mb={1}>
                          <Box mb={1}>{t('onboarding.mechanics.mechanic_step_prefilled_mechanic')}</Box>
                          <Grid container direction='row' justifyContent='space-between' sx={{ mb: 1 }}>
                            <Grid item xs={10}>
                              <Grid container spacing={1} direction='row'>
                                <Grid item xs={3}>
                                  <TextField
                                    disabled
                                    className='field'
                                    name='prefilledEmployee.FirstName'
                                    label={t('onboarding.mechanic.firstname')}
                                    required
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <TextField
                                    disabled
                                    className='field'
                                    name='prefilledEmployee.NamePrefix'
                                    label={t('onboarding.mechanic.prefix')}
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <TextField
                                    disabled
                                    className='field'
                                    name='prefilledEmployee.Name'
                                    label={t('onboarding.mechanic.name')}
                                    required
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <TextField
                                    disabled
                                    className='field'
                                    name='prefilledEmployee.EmailAddressWork'
                                    label={t('onboarding.mechanic.email')}
                                    required
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={2}>
                              <Grid container direction='row' justifyContent='flex-end'>
                                <Grid item xs={6}>
                                  <Box sx={{ ml: 1 }}>
                                    <ColorPicker disabled name='prefilledEmployee.ProfileColour' />
                                  </Box>
                                </Grid>
                                <Grid item xs={6}>
                                  <IconButton disabled size='large'>
                                    <Tooltip title={<>{t('onboarding.mechanic.cannot_remove_self')}</>}>
                                      <ClearIcon />
                                    </Tooltip>
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                      {values.Mechanics.length > 0 &&
                        values.Mechanics.map((_, index) => (
                          <Grid container direction='row' key={index} justifyContent='space-between' sx={{ mb: 1 }}>
                            <Grid item xs={10}>
                              <Grid container spacing={1} direction='row'>
                                <Grid item xs={3}>
                                  <TextField
                                    required
                                    className='field'
                                    name={`Mechanics[${index}].FirstName`}
                                    label={t('onboarding.mechanic.firstname')}
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <TextField
                                    className='field'
                                    name={`Mechanics[${index}].NamePrefix`}
                                    label={t('onboarding.mechanic.prefix')}
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <TextField
                                    required
                                    className='field'
                                    name={`Mechanics[${index}].Name`}
                                    label={t('onboarding.mechanic.name')}
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <TextField
                                    required
                                    className='field'
                                    name={`Mechanics[${index}].EmailAddressWork`}
                                    label={t('onboarding.mechanic.email')}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={2}>
                              <Grid container direction='row'>
                                <Grid item xs={6}>
                                  <Box sx={{ ml: 1 }}>
                                    <ColorPicker name={`Mechanics[${index}].ProfileColour`} />
                                  </Box>
                                </Grid>
                                <Grid item xs={6}>
                                  <IconButton onClick={() => remove(index)} size='large'>
                                    <ClearIcon />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        ))}
                      <Grid container justifyContent='flex-end'>
                        <Grid item xs={2}>
                          <Grid container justifyContent='flex-end'>
                            <Grid item xs={6}>
                              <IconButton
                                onClick={() =>
                                  push({ FirstName: '', Prefix: '', Name: '', EmailAddressWork: '', ProfileColour: '#cccccc' })
                                }
                                size='large'
                              >
                                <AddIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </FieldArray>

                <StepNavigation prevStep={prevStep} hideBack={isFirstStep} isLoading={isSubmitting || isPersisting} hideSkip={hideSkip} />
              </WithRunInBackgroundHelper>
            )}
          </Form>
        </Grid>
      </Grid>
    </BaseStepLayout>
  )
}

export default OnboardingAddMechanicsForm
